import useHorizontalDrag from "./useHorizontalDrag";
import { useCallback, useEffect, useRef, useState } from "react";

// Trackpad and touch input use native scrolling, including sub-card distances
// and OS momentum. Only the arrow buttons request an animated page movement.
export default function useCarouselScroll({
  itemCount,
  resetKey,
  onNearEnd,
  onInteraction
}) {
  const [track, setTrack] = useState(null);
  const [edges, setEdges] = useState({ previous: false, next: false });
  const callbacks = useRef({ onNearEnd, onInteraction });
  callbacks.current = { onNearEnd, onInteraction };

  const reset = useCallback(() => {
    if (track) track.scrollLeft = 0;
  }, [track]);

  useEffect(reset, [reset, resetKey]);

  useEffect(() => {
    if (!track) return;
    let idleTimer;
    const measure = () => {
      const position = Math.abs(track.scrollLeft);
      const remaining = track.scrollWidth - track.clientWidth - position;
      const previous = position > 2;
      const next = remaining > 2;
      setEdges((current) =>
        current.previous === previous && current.next === next
          ? current
          : { previous, next }
      );
      return { position, remaining };
    };
    const onScroll = () => {
      const { position, remaining } = measure();
      if (track.dataset.scrolling !== "true" && callbacks.current.onInteraction)
        callbacks.current.onInteraction();
      track.dataset.scrolling = "true";
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        delete track.dataset.scrolling;
      }, 180);
      // Fetch before reaching the end, without resetting the current position.
      if (
        position > 0 &&
        remaining <= track.clientWidth &&
        callbacks.current.onNearEnd
      )
        callbacks.current.onNearEnd();
    };
    measure();
    track.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", measure);
    const observer =
      typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(track);
    return () => {
      clearTimeout(idleTimer);
      delete track.dataset.scrolling;
      track.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", measure);
      if (observer) observer.disconnect();
    };
  }, [track, itemCount]);

  useHorizontalDrag(track);

  const move = (direction) => {
    if (!track) return;
    const step =
      track.firstElementChild?.getBoundingClientRect().width ||
      track.clientWidth;
    const distance = Math.max(1, Math.floor(track.clientWidth / step)) * step;
    track.scrollBy({
      left:
        direction *
        distance *
        (getComputedStyle(track).direction === "rtl" ? -1 : 1),
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth"
    });
  };

  return {
    trackRef: setTrack,
    canPrevious: edges.previous,
    canNext: edges.next,
    move,
    reset
  };
}
